import { withFormValidation } from '@updater-components/form-element';
import {
  withHTMLComponent,
  upgradeProperty,
  applyAsAttributes,
} from '@updater-legacy/components/custom-element';
import styles from './index.css';
import html from './index.html';

const SPACE_KEY = 32;

const ATTRIBUTES = [
  'checked',
  'disabled',
  'value',
  'required',
  'label',
  'helper',
  'name',
];

const ATTRIBUTE_TYPES = {
  checked: 'boolean',
  disabled: 'boolean',
  required: 'boolean',
};

class Checkbox extends withFormValidation(withHTMLComponent(styles, html)()) {
  static observedAttributes = ATTRIBUTES;
  static reflectedAttributes = ATTRIBUTES.filter((a) => a !== 'value');
  static attributeTypes = ATTRIBUTE_TYPES;

  constructor() {
    super();
    this._input = this.shadowRoot.querySelector('input[type="checkbox"]');
    this._label = this.shadowRoot.querySelector('#label');
    this._helper = this.shadowRoot.querySelector('#helper');
  }

  updated(
    { value, checked, disabled, required, label, helper, name },
    oldVals
  ) {
    applyAsAttributes(this._input, {
      value,
      name,
      required,
      disabled,
    });

    this._label.innerHTML = label || '';
    this._helper.innerHTML = helper || '';

    this.setAttribute('aria-disabled', disabled);
    if (disabled) {
      this.removeAttribute('tabindex');
      this.blur();
    } else {
      this.setAttribute('tabindex', 0);
    }

    if (checked !== oldVals.checked) {
      this._input.checked = checked;
      this.setAttribute('aria-checked', checked);
      this.checkValidity();
    }
  }

  connected() {
    upgradeProperty(this, 'value');

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'checkbox');
    }
    if (!this.hasAttribute('tabindex') && !this.disabled) {
      this.setAttribute('tabindex', 0);
    }

    this.addEventListener('click', this._handleClick);
    this.addEventListener('keyup', this._handleKeyPress);
    this.addEventListener('keydown', this._handleKeyPress);
    this._input.addEventListener('click', this._handleInputClick);
    this._input.addEventListener('invalid', this._handleInvalid);
  }

  disconnected() {
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keyup', this._handleKeyPress);
    this.removeEventListener('keydown', this._handleKeyPress);
    this._input.removeEventListener('click', this._handleInputClick);
    this._input.addEventListener('invalid', this._handleInvalid);
  }

  get value() {
    return this.checked;
  }

  set value(newValue) {
    this.checked = newValue;
  }

  _handleCheckChange() {
    // Fire change event to mimic native functionality
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: this.value,
      })
    );
  }

  _handleInputClick = (event) => {
    // We stopPropagation and preventDefault on the input
    // so the component click handle won't be called twice,
    // therefore negating the checked toggle since we want
    // this component to handle the checked state and drive
    // the input.
    event.preventDefault();
    event.stopPropagation();
  };

  _handleClick = () => {
    if (!this.disabled) {
      this.checked = !this.checked;
      this._handleCheckChange();
    }
  };

  _handleKeyPress = (event) => {
    // Don’t handle modifier shortcuts typically used by assistive technology.
    if (event.altKey || this.disabled) {
      return;
    }

    if (event.keyCode === SPACE_KEY) {
      // Prevent scrolling on space key press
      event.preventDefault();

      if (event.type === 'keyup') {
        this._handleClick();
      }
    }
  };

  _handleInvalid = (event) => {
    // The `invalid` event does not bubble so we re-dispatch it
    // to mimic the native checkbox functionality
    this.dispatchEvent(new Event(event.type, event));
  };
}

customElements.define('upd-checkbox', Checkbox);
